<template>
<!-- eslint-disable max-len -->
<collapsible-section
  :name="name"
  :id="idFromName"
>
  <array-input
    :items="branchPointRules"
    :validator="validator"
    :disabled="validator.$invalid && branchPointRules.length > 0"
    :required="required"
    :sections="sections"
    :stub="{ metricAdjustments: [], narratives: [] }"
  >
    <h4 slot="title"></h4>

    <template slot="item" slot-scope="{ item: rule, index: i }">
      <select-input
        label
        v-model="rule.next"
        :options="decisionOptions"
        use="_id"
        display="name"
        :to="(id) => { return `/blade/major-decisions/${id}/edit`; }"
        :remove="() => { remove(i); }"
        :up="() => { up(i); }"
        :down="() => { down(i); }"
      >Rule's Next Major Decision (if true)</select-input>

      <div>Metric-Based Rule</div>
      <div class="form-row">
        <select-input
          v-if="rule.type !== 'always'"
          v-model="rule.metric"
          :options="metricOptions"
          use="_id"
          display="name"
          :to="(id) => { return `/blade/metrics/${id}/edit`; }"
          required
        >Dependent Metric</select-input>

        <select-input
          v-model="rule.type"
          :options="ruleTypes"
          :display="ruleTypeToDisplayName"
          required
        >Rule Type (comparison)</select-input>

        <number-input
          v-if="rule.type !== 'always'"
          v-model="rule.value"
        >{{['withinRange', 'outsideRange'].includes(rule.type) ? 'Min Value' : 'Value'}}</number-input>

        <number-input
          v-if="['withinRange', 'outsideRange'].includes(rule.type)"
          v-model="rule.value2"
        >Max Value</number-input>
      </div>

      <collapsible-section
        name="Additional Metric-Based Conditions"
        :id="`BP${i}-AMBC`"
        bold
      >
        <array-input :items="rule.additionalConditions" :disabled="validator.$each[i].additionalConditions.$invalid">
          <h5 slot="title"></h5>

          <template slot="item" slot-scope="{ item: additionalRule, index: r }">
            <div class="form-row">
              <select-input
                v-if="additionalRule.type !== 'always'"
                v-model="additionalRule.metric"
                :options="metricOptions"
                use="_id"
                display="name"
                :to="(id) => { return `/blade/metrics/${id}/edit`; }"
                required
              >Dependent Metric</select-input>

              <select-input
                v-model="additionalRule.type"
                :options="ruleTypes"
                :display="ruleTypeToDisplayName"
                required
              >Rule Type (comparison)</select-input>

              <number-input
                v-if="additionalRule.type !== 'always'"
                v-model="additionalRule.value"
              >{{['withinRange', 'outsideRange'].includes(additionalRule.type)  ? 'Min Value' : 'Value'}}</number-input>

              <number-input
                v-if="['withinRange', 'outsideRange'].includes(additionalRule.type)"
                v-model="additionalRule.value2"
              >Max Value</number-input>
            </div>
          </template>
          <span slot="button">Add Condition</span>
        </array-input>
      </collapsible-section>

      <collapsible-section
        name="Metric Adjustments"
        :id="`BP${i}-MA`"
        bold
      >
        <array-input :items="rule.metricAdjustments" :disabled="validator.$each[i].metricAdjustments.$invalid">
          <h5 slot="title"></h5>

          <template slot="item" slot-scope="{ item: metricAdjustment, index: k }">
            <div class="form-row">
              <select-input
                class="col-sm-6"
                v-model="metricAdjustment.metric"
                :validator="validator.$each[i].metricAdjustments.$each[k].metric"
                :options="unused(metricOptions).by('_id').in(rule.metricAdjustments).by('metric').at(k)"
                use="_id"
                :display="m => { return `${m.name} (${m.type})`; }"
                required
                autofocus
                :remove="() => { rule.metricAdjustments.splice(k, 1); }"
                :to="(id) => { return `/blade/metrics/${id}/edit`; }"
              >
                Metric
              </select-input>

              <number-input class="col-sm-3" v-model="metricAdjustment.adjustment">Adjustment</number-input>

              <checkbox-input
                class="col-sm-2"
                v-model="metricAdjustment.setToValue"
              >
                Set
              </checkbox-input>
            </div>
          </template>
          <span slot="button">Add Metric Adjustment</span>
        </array-input>
      </collapsible-section>

      <collapsible-section
        name="Narrative"
        :id="`BP${i}-N`"
        bold
      >
        <narrative-input
          :narratives="rule.narratives"
          :validator="validator.$each[i].narratives"
          :narrative-types="narrativeTypes"
          :speakers="speakers"
          :characters="characters"
          :variants="variants"
          no-title
        >Narrative</narrative-input>
      </collapsible-section>

      <hr style="border: 1px solid #aaa;">
    </template>
    <span slot="button">Add Rule</span>
  </array-input>
</collapsible-section>
</template>

<script>
import { toTitleCase } from '../../../utils/text';
import { unused } from '../../../utils/utils';

import ArrayInput from '../inputs/ArrayInput';
import CollapsibleSection from './CollapsibleSection';
import SelectInput from '../inputs/SelectInput';
import NumberInput from '../inputs/NumberInput';
import CheckboxInput from '../inputs/CheckboxInput';
import NarrativeInput from '../inputs/NarrativeInput';

export default {
  props: {
    branchPointRules: { type: Array, required: true },
    validator: { type: Object, required: true },
    decisionOptions: { type: Array, required: true },
    metricOptions: { type: Array, required: true },
    required: { type: Boolean },
    sections: { type: Array },
    name: { type: String, default: 'Branch Point Rules (optional)' },
    narrativeTypes: { type: Array, required: true },
    speakers: { type: Array, required: true },
    characters: { type: Array, required: true },
    variants: { type: Array, required: true },
  },
  components: {
    ArrayInput,
    CollapsibleSection,
    SelectInput,
    NumberInput,
    CheckboxInput,
    NarrativeInput,
  },
  data() {
    return {
      unused,
      ruleTypes: [
        'always',
        'lessThan',
        'lessThanOrEqual',
        'exact',
        'greaterThanOrEqual',
        'greaterThan',
        'withinRange',
        'outsideRange',
      ],
    };
  },
  computed: {
    idFromName() {
      return this.name.replace(/\s/g, '');
    }
  },
  methods: {
    up(index) {
      if (index <= 0) return;
      const [item] = this.branchPointRules.splice(index, 1);
      this.branchPointRules.splice(index - 1, 0, item);
    },
    down(index) {
      if (index >= this.branchPointRules.length - 1) return;
      const [item] = this.branchPointRules.splice(index, 1);
      this.branchPointRules.splice(index + 1, 0, item);
    },
    remove(index) {
      this.branchPointRules.splice(index, 1);
    },
    ruleTypeToDisplayName(t) {
      return toTitleCase(t);
    },
  },
  created() {
  },
  mounted() {
  },
  watch: {
  }
};
</script>

<style lang="scss" scoped>
.form-drawer {
  margin: 0 200px 10px 1px;
  padding-left: 20px;
  border-left: 1px solid lightgrey;
  border-bottom: 1px solid lightgrey;
}
</style>



// WEBPACK FOOTER //
// src/components/ui/inputs/BranchPointRulesInput.vue